import { useState, useRef, useCallback, useEffect } from "react";

const SpeechRecognition = typeof window !== "undefined"
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

export const isSpeechSupported = !!SpeechRecognition;

export function useSpeechRecognition({ lang = "fr-FR", onResult, onEnd } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onEndRef = useRef(onEnd);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);

  const stop = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognition) {
      setError("not-supported");
      return;
    }
    setError(null);

    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    let lastSpeechAt = Date.now();

    recognition.onresult = (event) => {
      lastSpeechAt = Date.now();
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      if (onResultRef.current) {
        onResultRef.current({ finalText, interimText });
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "permission-denied") {
        setError("permission");
      } else if (event.error === "network") {
        setError("network");
      } else if (event.error === "no-speech") {
        // Timeout without speech — handled by our own timer
      } else if (event.error !== "aborted") {
        setError(event.error);
      }
      stop();
    };

    recognition.onend = () => {
      setListening(false);
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (onEndRef.current) onEndRef.current();
    };

    try {
      recognition.start();
      setListening(true);

      // Silence timeout: 5 seconds without speech → auto stop
      const checkSilence = () => {
        if (Date.now() - lastSpeechAt > 5000) {
          stop();
        } else {
          timeoutRef.current = setTimeout(checkSilence, 1000);
        }
      };
      timeoutRef.current = setTimeout(checkSilence, 5000);
    } catch (e) {
      setError("start-failed");
      setListening(false);
    }
  }, [lang, stop]);

  const toggle = useCallback(() => {
    if (listening) { stop(); } else { start(); }
  }, [listening, start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) { /* ok */ }
      }
    };
  }, []);

  return { listening, error, toggle, stop, supported: isSpeechSupported };
}
